import { DateRangePickerAlt, type DateRangePickerAltProps } from '@SBGSports/catapult-core';
import { useGetSetting, KEY_DATE_LOCALE, KEY_DEFAULT_LANGUAGE_WEB } from 'api';
import { getLocale, useDateFormat } from 'shared/utils/date-time-utils';
import { usePickerLabels } from './use-picker-labels';
import { assertValidDateProps } from './validate-date-props';

/**
 * Props for {@link DateRangePicker}. Accepts every prop on the design system's
 * `DateRangePickerAltProps` except `locale`, `format`, and `labels`, which the
 * wrapper injects from app settings and translations.
 *
 * Cmd+Click `DateRangePickerAltProps` in the import above to see the full prop
 * list, including `quickSelectOptions` (built-in IDs) and
 * `customQuickSelectOptions` (pair with {@link useDefaultQuickSelectOptions}).
 */
export type DateRangePickerProps = Omit<DateRangePickerAltProps, 'locale' | 'format' | 'labels'>;

/**
 * Openfield date range picker. Thin wrapper around the design system's
 * `DateRangePickerAlt` that injects app context and applies sensible defaults.
 *
 * **Auto-injected (not overridable):**
 * - `locale`: from `KEY_DATE_LOCALE` + `KEY_DEFAULT_LANGUAGE_WEB` settings
 * - `format`: from `useDateFormat()` (dd/MM/yyyy or MM/dd/yyyy)
 * - `labels`: translated via `date_picker.*` i18n keys
 *
 * **Defaults (overridable via props):**
 * - `weekStart={1}` (Monday)
 * - `showClearButton`
 * - `commitMode="auto"`
 *
 * Quick selects are opt-in. Pass `customQuickSelectOptions` with
 * {@link useDefaultQuickSelectOptions} for the translated default set, or
 * `quickSelectOptions` with built-in IDs to show a subset.
 *
 * @example
 * ```tsx
 * const [range, setRange] = useState<DateRangeValue | undefined>();
 * const quickSelects = useDefaultQuickSelectOptions();
 * <DateRangePicker
 *     value={range}
 *     onChange={setRange}
 *     customQuickSelectOptions={quickSelects}
 * />
 * ```
 */
export const DateRangePicker = (props: DateRangePickerProps) => {
    // Fail fast on bad Date props before any hooks run
    assertValidDateProps('DateRangePicker', {
        'value.start': props.value?.start,
        'value.end': props.value?.end,
        minDate: props.minDate,
        maxDate: props.maxDate,
    });

    const language = useGetSetting(KEY_DEFAULT_LANGUAGE_WEB)?.value ?? '';
    const dateLocale = useGetSetting(KEY_DATE_LOCALE)?.value ?? '';
    const locale = getLocale(language, dateLocale);
    const format = useDateFormat();
    const labels = usePickerLabels();

    return (
        <DateRangePickerAlt
            weekStart={1}
            showClearButton
            commitMode="auto"
            // Uppercased format as the default placeholder (DD/MM/YYYY). Overridable via props.
            placeholder={format.toUpperCase()}
            {...props}
            locale={locale}
            format={format}
            labels={labels}
        />
    );
};
